import React from 'react';
import '../Terms/Terms.scss';
import {NavLink} from "react-router-dom";
import {LEGAL} from "../../constants/legal";

const {companyName, serviceName, website, contactEmail, jurisdiction, minimumAge, lastUpdated} = LEGAL;

// Describes what the app actually does with data. Update it when a provider or data flow changes.
const Privacy = () => {
    return (
        <div className="Terms">
            <article className="Terms__content">
                <header>
                    <h1>{serviceName} Privacy Policy</h1>
                    <p className="Terms__updated">Last updated: {lastUpdated}</p>
                </header>

                <p className="Terms__highlight">
                    <strong>In short:</strong> we use the chats, photos and voice samples you upload only to create
                    and run your AI chats. We don't sell your data or use it to train AI models. You can delete a
                    chat, or your whole account, at any time from the app.
                </p>

                <section>
                    <h2>1. Who we are</h2>
                    <p>
                        {serviceName} ({website}) is operated by {companyName} ("we", "us"). We are responsible for
                        the personal data described in this policy. Contact: {contactEmail}.
                    </p>
                </section>

                <section>
                    <h2>2. What we collect</h2>
                    <ul>
                        <li><strong>Account data:</strong> your name, email address and sign-in details (handled by
                            Google Firebase Authentication; we never see your password).
                        </li>
                        <li><strong>Content you upload:</strong> chat exports from WhatsApp or Telegram, the photo
                            and voice sample you add, and the settings of each AI chat.
                        </li>
                        <li><strong>Conversations:</strong> the messages you send to your AI chats and the replies,
                            including generated voice messages.
                        </li>
                        <li><strong>Usage and billing:</strong> counts of messages, voice replies and AI usage per
                            month, and your subscription status. Payments are handled by Stripe; we never store your
                            card number.
                        </li>
                        <li><strong>Analytics (optional):</strong> if you accept analytics cookies, Google Analytics
                            collects information about how you use the website. Without your consent, no analytics
                            cookies are set.
                        </li>
                    </ul>
                </section>

                <section>
                    <h2>3. Other people in your chats</h2>
                    <p>
                        Chat exports contain messages from other people. Before uploading, you confirm that you have
                        the right to use that chat, photo and voice, and that the person has agreed or has passed
                        away and you are a close family member. We use their messages only to build the AI chat you
                        create, which only you can see. If you believe your data was uploaded without permission,
                        contact {contactEmail} and we will remove it.
                    </p>
                </section>

                <section>
                    <h2>4. How we use your data</h2>
                    <ul>
                        <li>To create your AI chats and generate replies and voice messages (performing our contract
                            with you).
                        </li>
                        <li>To manage your account, subscription, plan limits and payments.</li>
                        <li>To keep the service secure and prevent abuse, for example with rate limits.</li>
                        <li>To understand and improve the website, only if you accepted analytics cookies.</li>
                    </ul>
                    <p>We don't sell your data, show ads, or use your content to train AI models.</p>
                </section>

                <section>
                    <h2>5. Service providers we use</h2>
                    <p>These companies process data on our behalf, only to run the service:</p>
                    <ul>
                        <li><strong>Google Firebase</strong>: sign-in, database and file storage.</li>
                        <li><strong>OpenAI</strong>: generating chat replies.</li>
                        <li><strong>ElevenLabs</strong>: cloning the voice from your sample and generating voice
                            replies.
                        </li>
                        <li><strong>Weaviate</strong>: storing your uploaded chat so replies can draw on shared
                            memories.
                        </li>
                        <li><strong>Stripe</strong>: payments and subscriptions.</li>
                        <li><strong>Vercel and Heroku</strong>: hosting the website and the server.</li>
                        <li><strong>Sentry</strong>: error reports when something breaks, so we can fix it. Reports
                            include technical details and your account ID, never your chats, voice or password.
                        </li>
                        <li><strong>Google Analytics</strong>: website analytics, only with your consent.</li>
                    </ul>
                    <p>
                        Some of these providers are based in, or process data in, the United States. Where required,
                        transfers are covered by the providers' standard data protection terms.
                    </p>
                </section>

                <section>
                    <h2>6. How long we keep data</h2>
                    <ul>
                        <li>Your chats, uploads and messages are kept until you delete the chat or your account.</li>
                        <li>Deleting a chat removes its messages, uploaded files, stored chat history and cloned voice
                            from our systems right away. Deleting your account also removes your profile and cancels
                            your subscription.
                        </li>
                        <li>Some providers keep limited copies for a short time for security and abuse monitoring
                            (for example, OpenAI keeps API data for up to 30 days). Stripe keeps payment records as
                            required by law.
                        </li>
                    </ul>
                </section>

                <section>
                    <h2>7. Your rights</h2>
                    <p>
                        Depending on where you live, you can ask to access, correct, export or delete your data, and
                        object to or restrict how we use it. You can delete chats and your account yourself in the
                        app. For anything else, email {contactEmail}. You may also complain to your local data
                        protection authority.
                    </p>
                </section>

                <section>
                    <h2>8. Security</h2>
                    <p>
                        Your data is sent over encrypted connections and is only accessible to your account and to
                        the providers listed above. No system is perfectly secure, so please don't upload content you
                        are not comfortable storing online.
                    </p>
                </section>

                <section>
                    <h2>9. Age</h2>
                    <p>{serviceName} is only for people aged {minimumAge} or older.</p>
                </section>

                <section>
                    <h2>10. Changes</h2>
                    <p>
                        We may update this policy. If the changes are significant, we will tell you in the app or by
                        email. This policy is governed by the laws of {jurisdiction}.
                    </p>
                </section>

                <NavLink to={'/'}>Back to {serviceName}</NavLink>
            </article>
        </div>
    );
};

export default Privacy;
